import { useState, useEffect, useRef, useCallback } from 'react';

interface LocalTimerState {
  taskId: string;
  taskTitle: string;
  startTime: number;
  pausedTime: number;
  totalPausedMs: number;
  isRunning: boolean;
  isOnBreak: boolean;
  breakStartTime: number | null;
}

const STORAGE_KEY = 'goalie4-timer-state';

// Get initial state from localStorage
function getStoredState(): LocalTimerState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load timer state:', error);
  }
  return null;
}

// Custom event for same-tab synchronization
const TIMER_UPDATE_EVENT = 'goalie4-timer-update';

// Save state to localStorage and dispatch event
function saveState(state: LocalTimerState | null) {
  console.log('[Timer] Saving state:', state ? { taskId: state.taskId, isRunning: state.isRunning } : 'null');
  try {
    if (state) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      console.log('[Timer] Removing timer from localStorage');
      localStorage.removeItem(STORAGE_KEY);
      // Double-check it was removed
      const check = localStorage.getItem(STORAGE_KEY);
      if (check) {
        console.error('[Timer] Failed to remove timer state, still exists:', check);
      }
    }
    
    // Dispatch custom event for same-tab synchronization
    window.dispatchEvent(new CustomEvent(TIMER_UPDATE_EVENT, { 
      detail: { state } 
    }));
  } catch (error) {
    console.error('Failed to save timer state:', error);
  }
}

export function useLocalTimer() {
  const [timerState, setTimerState] = useState<LocalTimerState | null>(() => {
    const stored = getStoredState();
    if (stored) {
      console.log('[Timer] Initial state loaded from localStorage:', { taskId: stored.taskId, isRunning: stored.isRunning });
    }
    return stored;
  });
  const [displayTime, setDisplayTime] = useState('00:00:00');
  const animationRef = useRef<number>();
  const lastUpdateRef = useRef<number>(0);
  
  // Start timer for a task
  const startTimer = useCallback((taskId: string, taskTitle: string) => {
    const newState: LocalTimerState = {
      taskId,
      taskTitle,
      startTime: Date.now(),
      pausedTime: 0,
      totalPausedMs: 0,
      isRunning: true,
      isOnBreak: false,
      breakStartTime: null
    };
    setTimerState(newState);
    saveState(newState);
  }, []);
  
  // Stop timer and return elapsed time
  const stopTimer = useCallback((): number => {
    console.log('[Timer] stopTimer called, current state:', timerState);
    if (!timerState) return 0;
    
    const elapsed = Date.now() - timerState.startTime - timerState.totalPausedMs;
    console.log('[Timer] Calculated elapsed time:', elapsed, 'ms');
    setTimerState(null);
    saveState(null);
    return elapsed;
  }, [timerState]);
  
  // Pause/resume timer
  const togglePause = useCallback(() => {
    if (!timerState) return;
    
    const now = Date.now();
    const newState = { ...timerState };
    
    if (timerState.isRunning) {
      // Pause
      newState.isRunning = false;
      newState.pausedTime = now;
    } else {
      // Resume
      newState.isRunning = true;
      newState.totalPausedMs += now - timerState.pausedTime;
    }
    
    setTimerState(newState);
    saveState(newState);
  }, [timerState]);
  
  // Take a break
  const startBreak = useCallback(() => {
    if (!timerState || !timerState.isRunning) return;
    
    const newState = {
      ...timerState,
      isOnBreak: true,
      breakStartTime: Date.now()
    };
    
    setTimerState(newState);
    saveState(newState);
  }, [timerState]);
  
  // End break
  const endBreak = useCallback(() => {
    if (!timerState || !timerState.isOnBreak) return;
    
    const breakDuration = Date.now() - (timerState.breakStartTime || 0);
    const newState = {
      ...timerState,
      isOnBreak: false,
      breakStartTime: null,
      totalPausedMs: timerState.totalPausedMs + breakDuration
    };
    
    setTimerState(newState);
    saveState(newState);
  }, [timerState]);
  
  // Update display time using requestAnimationFrame
  useEffect(() => {
    const updateDisplay = (timestamp: number) => {
      // Throttle to update only every 100ms
      if (timestamp - lastUpdateRef.current < 100) {
        animationRef.current = requestAnimationFrame(updateDisplay);
        return;
      }
      lastUpdateRef.current = timestamp;
      
      if (timerState && timerState.isRunning && !timerState.isOnBreak) {
        const elapsed = Date.now() - timerState.startTime - timerState.totalPausedMs;
        const hours = Math.floor(elapsed / 3600000);
        const minutes = Math.floor((elapsed % 3600000) / 60000);
        const seconds = Math.floor((elapsed % 60000) / 1000);
        
        const newTime = `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        
        setDisplayTime(current => current !== newTime ? newTime : current);
      }
      
      if (timerState && timerState.isRunning) {
        animationRef.current = requestAnimationFrame(updateDisplay);
      }
    };
    
    if (timerState && timerState.isRunning) {
      animationRef.current = requestAnimationFrame(updateDisplay);
    } else if (!timerState || !timerState.isRunning) {
      setDisplayTime('00:00:00');
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [timerState]);
  
  // Sync with other tabs via storage event and same-tab via custom event
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const newState = e.newValue ? JSON.parse(e.newValue) : null;
        setTimerState(newState);
      }
    };
    
    const handleTimerUpdate = (e: CustomEvent) => {
      setTimerState(e.detail.state);
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(TIMER_UPDATE_EVENT, handleTimerUpdate as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(TIMER_UPDATE_EVENT, handleTimerUpdate as EventListener);
    };
  }, []);
  
  // Force clear timer (for debugging/emergency use)
  const forceClearTimer = useCallback(() => {
    console.log('[Timer] Force clearing timer state');
    setTimerState(null);
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(TIMER_UPDATE_EVENT, { 
      detail: { state: null } 
    }));
  }, []);

  return {
    // State
    isRunning: timerState?.isRunning ?? false,
    isOnBreak: timerState?.isOnBreak ?? false,
    currentTaskId: timerState?.taskId,
    currentTaskTitle: timerState?.taskTitle,
    displayTime,
    
    // Actions
    startTimer,
    stopTimer,
    togglePause,
    startBreak,
    endBreak,
    forceClearTimer,
    
    // Check if timer is running for a specific task
    isTimerRunningForTask: (taskId: string) => timerState?.taskId === taskId
  };
}

export type LocalTimerApi = ReturnType<typeof useLocalTimer>;
